import fulfillmentFixtures from '../../fixtures/fulfillment.json';
import itemsFixture from '../../fixtures/items.json';
import sitesFixture from '../../fixtures/sites.json';
import {
  lineComplete,
  linePicked,
  type AmendLineInput,
  type FulfillmentOrder,
  type FulfillmentOrderCreateInput,
  type FulfillmentOrderLine,
  type FulfillmentOrderPatch,
  type FulfillmentPick,
  type FulfillmentReason,
  type FulfillmentTemplate,
  type FulfillmentTemplateInput,
  type PickInput,
  type PickResult,
  type StockBalance,
  type StockItem,
  type WarehouseLocation,
  type HandoverMethod,
} from '../domain/fulfillment';
import {
  fulfillmentOrderSchema,
  fulfillmentTemplateSchema,
  stockItemSchema,
  warehouseLocationSchema,
} from '../domain/validation';
import {
  ConflictError,
  DuplicateError,
  FulfillmentError,
  NotFoundError,
  ValidationError,
} from './errors';
import type { FulfillmentApi, FulfillmentOrderFilters } from './fulfillment-api';
import type { StockApi, StockBalanceFilters, StockMovementFilters } from './stock-api';
import {
  normalizeBarcode,
  stockItemMatchesCode,
  type StockCommand,
  type StockCommandResult,
  type StockItemInput,
  type StockMovement,
  type StockMovementPage,
  type WarehouseLocationInput,
} from '../domain/stock';

const MOCK_USER = 'mock.current-user@example.test';
const UNSHIPPABLE = new Set(['SCRAP', 'NEEDS_REPAIR']);

let mockCounter = 0;
function mockId(prefix: string): string {
  mockCounter += 1;
  return `${prefix}-${Date.now()}-${mockCounter}`;
}

const wait = async (milliseconds = 80): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const clone = <T>(value: T): T => structuredClone(value);

function reject(reason: FulfillmentReason, detail: string): never {
  throw new FulfillmentError(`${reason}: ${detail}`, reason);
}

function normalizeSerial(code: string): string {
  return code.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function normalizeSku(code: string): string {
  return code.trim().replace(/\s+/g, ' ').toUpperCase();
}

interface MockItem {
  id: string;
  partId: string;
  siteId: string;
  status?: string | undefined;
}

interface FixtureOrder {
  dispatchInDays?: number;
  picks?: (Record<string, unknown> & { pickedAtHoursAgo?: number })[];
  lines: Record<string, unknown>[];
  [key: string]: unknown;
}

/**
 * Deterministic pick-pack mock. It mirrors the backend's rules
 * (eel/leatherback/db/postgres/dao/fulfillment.go) closely enough for the
 * UI and e2e tests: serial matching ignoring case and punctuation, one open
 * order per Item, all-or-nothing release, idempotent picks. Handover
 * changes this mock's Item copies only, not the inventory mock.
 */
export class MockFulfillmentApi implements FulfillmentApi, StockApi {
  private items = new Map<string, MockItem>();
  private itemsByNorm = new Map<string, MockItem[]>();
  private stockItems: StockItem[];
  // Photos as data URLs, keyed by stock item id.
  private stockImages = new Map<string, string>();
  private nextImageId = 1;
  private locations: WarehouseLocation[];
  private onHand = new Map<string, number>();
  /** The stock ledger, oldest first. */
  private movements: StockMovement[] = [];
  /** Stock command idempotency key -> [fingerprint, movement id]. */
  private commandKeys = new Map<string, [string, string]>();
  private templates: FulfillmentTemplate[];
  private orders: FulfillmentOrder[];
  /** Item ID -> order ID, for Items picked on open orders. */
  private reservations = new Map<string, string>();
  /** Order line ID -> reserved stock quantity. */
  private allocations = new Map<
    string,
    { stockItemId: string; locationId: string; quantity: number }
  >();
  /** Pick idempotency key -> request fingerprint, per order. */
  private pickKeys = new Map<string, string>();
  private pickIdForKey = new Map<string, string>();

  constructor() {
    for (const raw of itemsFixture as MockItem[]) {
      const item = { id: raw.id, partId: raw.partId, siteId: raw.siteId, status: raw.status };
      this.items.set(item.id, item);
      const norm = normalizeSerial(item.id);
      this.itemsByNorm.set(norm, [...(this.itemsByNorm.get(norm) ?? []), item]);
    }
    this.stockItems = fulfillmentFixtures.stockItems.map((row) => stockItemSchema.parse(row));
    this.locations = fulfillmentFixtures.locations.map((row) => warehouseLocationSchema.parse(row));
    for (const b of fulfillmentFixtures.onHand) {
      this.onHand.set(`${b.stockItemId}|${b.locationId}`, b.onHand);
      if (b.onHand > 0) {
        this.movements.push({
          id: mockId('movement'),
          stockItemId: b.stockItemId,
          quantity: b.onHand,
          toLocationId: b.locationId,
          reason: 'OPENING_BALANCE',
          referenceType: 'MOCK_SEED',
          actor: 'lager@tidalx.ai',
          createTime: new Date(Date.now() - 14 * 86_400_000).toISOString(),
        });
      }
    }
    this.templates = fulfillmentFixtures.templates.map((row) =>
      fulfillmentTemplateSchema.parse(row),
    );

    const now = Date.now();
    this.orders = (fulfillmentFixtures.orders as FixtureOrder[]).map((raw) => {
      const { dispatchInDays, picks, ...rest } = raw;
      const dispatch =
        dispatchInDays === undefined ? undefined : new Date(now + dispatchInDays * 86_400_000);
      // Local 08:00, so "today" means today wherever the tests run.
      if (dispatch) dispatch.setHours(8, 0, 0, 0);
      return fulfillmentOrderSchema.parse({
        ...rest,
        dispatchDate: dispatch?.toISOString(),
        createTime: new Date(now - 7 * 86_400_000).toISOString(),
        createdBy: 'lager@tidalx.ai',
        version: 3,
        picks: (picks ?? []).map(({ pickedAtHoursAgo, ...pick }) => ({
          ...pick,
          pickedAt: new Date(now - (pickedAtHoursAgo ?? 1) * 3_600_000).toISOString(),
        })),
      });
    });
    for (const order of this.orders) {
      if (order.state === 'DRAFT' || order.state === 'COMPLETED' || order.state === 'CANCELLED')
        continue;
      for (const line of order.lines) {
        for (const itemId of line.itemIds) this.reservations.set(itemId, order.id);
        if (line.requirement.type === 'STOCK' && line.locationId && line.requirement.stockItemId) {
          this.allocations.set(line.id, {
            stockItemId: line.requirement.stockItemId,
            locationId: line.locationId,
            quantity: line.requirement.quantity,
          });
          line.allocationId = `alloc-${line.id}`;
        }
      }
    }
  }

  private allocated(stockItemId: string, locationId: string): number {
    let total = 0;
    for (const a of this.allocations.values()) {
      if (a.stockItemId === stockItemId && a.locationId === locationId) total += a.quantity;
    }
    return total;
  }

  /** The active location with the most of a stock item free, if any has some. */
  private mostAvailable(stockItemId: string, free: (loc: string) => number): string | undefined {
    return this.locations
      .filter((l) => l.active && free(l.id) > 0)
      .sort((a, b) => free(b.id) - free(a.id))[0]?.id;
  }

  /** Reserves what has arrived of a waiting line's shortfall. */
  private topUp(line: FulfillmentOrderLine): void {
    const stockItemId = line.requirement.stockItemId;
    const waiting = line.waitingQuantity ?? 0;
    if (!stockItemId || waiting <= 0) return;
    const free = (loc: string) => this.available(stockItemId, loc);
    const locationId = line.locationId ?? this.mostAvailable(stockItemId, free);
    if (!locationId) return;
    const take = Math.min(waiting, Math.max(0, free(locationId)));
    if (take === 0) return;
    const held = this.allocations.get(line.id)?.quantity ?? 0;
    this.allocations.set(line.id, { stockItemId, locationId, quantity: held + take });
    line.locationId = locationId;
    line.allocationId = `alloc-${line.id}`;
    line.waitingQuantity = waiting - take || undefined;
  }

  private available(stockItemId: string, locationId: string): number {
    return (
      (this.onHand.get(`${stockItemId}|${locationId}`) ?? 0) -
      this.allocated(stockItemId, locationId)
    );
  }

  private order(id: string): FulfillmentOrder {
    const order = this.orders.find((o) => o.id === id);
    if (!order) throw new NotFoundError('Fulfillment order not found');
    return order;
  }

  private checkVersion(order: FulfillmentOrder, version: number): void {
    if (order.version !== version) {
      throw new ConflictError(
        'fulfillment order was changed by someone else; reload and try again',
        order.version,
      );
    }
  }

  private touch(order: FulfillmentOrder): FulfillmentOrder {
    order.version += 1;
    order.lastModified = new Date().toISOString();
    order.lastModifiedBy = MOCK_USER;
    return clone(order);
  }

  private validateLines(lines: FulfillmentOrderCreateInput['lines']): void {
    for (const line of lines) {
      const r = line.requirement;
      if (r.type === 'STOCK') {
        const item = this.stockItems.find((s) => s.id === r.stockItemId);
        if (!item) reject('INVALID_REFERENCE', `stock item ${r.stockItemId ?? ''} not found`);
        if (!item.active) reject('INACTIVE', `stock item ${item.sku}`);
      }
      if (r.quantity <= 0) throw new ValidationError('quantity must be positive');
    }
  }

  private toLines(lines: FulfillmentOrderCreateInput['lines']): FulfillmentOrderLine[] {
    return lines.map((line) => ({
      id: mockId('line'),
      requirement: clone(line.requirement),
      locationId: line.requirement.type === 'STOCK' ? line.locationId || undefined : undefined,
      itemIds: [],
      pickedQuantity: 0,
    }));
  }

  async listOrders(filters?: FulfillmentOrderFilters): Promise<FulfillmentOrder[]> {
    await wait();
    return clone(
      this.orders.filter(
        (o) =>
          (!filters?.states?.length || filters.states.includes(o.state)) &&
          (!filters?.siteId || o.siteId === filters.siteId) &&
          (!filters?.customerId || o.customerId === filters.customerId) &&
          (!filters?.fieldWorkRequestId || o.fieldWorkRequestId === filters.fieldWorkRequestId),
      ),
    );
  }

  async getOrder(id: string): Promise<FulfillmentOrder> {
    await wait();
    return clone(this.order(id));
  }

  async createOrder(input: FulfillmentOrderCreateInput): Promise<FulfillmentOrder> {
    await wait();
    const site = (sitesFixture as { id: string; customerId: string; name: string }[]).find(
      (s) => s.id === input.siteId,
    );
    if (!site) reject('INVALID_REFERENCE', 'site not found');
    if (site.customerId !== input.customerId)
      reject('INVALID_REFERENCE', `site ${site.name} belongs to another customer`);
    let templateLines: FulfillmentOrderCreateInput['lines'] = [];
    let templateVersion: number | undefined;
    if (input.templateId) {
      const template = this.templates.find((t) => t.id === input.templateId);
      if (!template) reject('INVALID_REFERENCE', 'template not found');
      if (!template.active) reject('INACTIVE', `template ${template.name}`);
      templateLines = template.requirements.map((requirement) => ({ requirement }));
      templateVersion = template.version;
    }
    const lines = [...templateLines, ...input.lines];
    if (lines.length === 0) throw new ValidationError('an order needs a template_id or lines');
    this.validateLines(lines);
    const now = new Date().toISOString();
    const order: FulfillmentOrder = {
      id: mockId('order'),
      externalReference: input.externalReference || undefined,
      customerId: input.customerId,
      siteId: input.siteId,
      fieldWorkRequestId: input.fieldWorkRequestId || undefined,
      templateId: input.templateId || undefined,
      templateVersion,
      state: 'DRAFT',
      dispatchDate: input.dispatchDate || undefined,
      notes: input.notes || undefined,
      lines: this.toLines(lines),
      picks: [],
      amendments: [],
      createdBy: MOCK_USER,
      createTime: now,
      version: 0,
      lastModified: now,
      lastModifiedBy: MOCK_USER,
    };
    this.orders.push(order);
    return clone(order);
  }

  async updateOrder(patch: FulfillmentOrderPatch): Promise<FulfillmentOrder> {
    await wait();
    const order = this.order(patch.id);
    this.checkVersion(order, patch.version);
    if (order.state === 'CANCELLED') reject('INVALID_STATE', "a cancelled order can't change");
    if (patch.lines !== undefined) {
      if (order.state !== 'DRAFT') reject('INVALID_STATE', 'lines can only change in DRAFT');
      this.validateLines(patch.lines);
      order.lines = this.toLines(patch.lines);
    }
    if (
      order.state === 'COMPLETED' &&
      (patch.externalReference !== undefined || patch.dispatchDate !== undefined)
    ) {
      reject('INVALID_STATE', 'a completed order only takes a shipment and notes');
    }
    if (patch.externalReference !== undefined)
      order.externalReference = patch.externalReference || undefined;
    if (patch.shipmentId !== undefined) order.shipmentId = patch.shipmentId || undefined;
    if (patch.dispatchDate !== undefined) order.dispatchDate = patch.dispatchDate ?? undefined;
    if (patch.notes !== undefined) order.notes = patch.notes || undefined;
    return this.touch(order);
  }

  async releaseOrder(
    id: string,
    version: number,
    allowShortfall = false,
  ): Promise<FulfillmentOrder> {
    await wait();
    const order = this.order(id);
    this.checkVersion(order, version);
    if (order.state !== 'DRAFT')
      reject('INVALID_STATE', `only a DRAFT order can be released, not ${order.state}`);
    // Plan every reservation first so a shortfall reserves nothing, unless
    // short lines may wait for the rest (tidaltech/koi#8511).
    const planned = new Map<
      string,
      { stockItemId: string; locationId: string | undefined; quantity: number }
    >();
    const pending = new Map<string, number>();
    for (const line of order.lines) {
      const r = line.requirement;
      if (r.type !== 'STOCK' || !r.stockItemId) continue;
      const free = (loc: string) =>
        this.available(r.stockItemId ?? '', loc) - (pending.get(`${r.stockItemId}|${loc}`) ?? 0);
      let locationId = line.locationId;
      if (!locationId) {
        locationId = this.locations
          .filter((l) => l.active && free(l.id) >= r.quantity)
          .sort((a, b) => free(b.id) - free(a.id))[0]?.id;
        if (!locationId && allowShortfall) locationId = this.mostAvailable(r.stockItemId, free);
        else if (!locationId)
          reject(
            'INSUFFICIENT_STOCK',
            `line "${r.label ?? ''}": ${this.stockName(r.stockItemId)}: ${r.quantity} needed, at most ${Math.max(0, ...this.locations.filter((l) => l.active).map((l) => free(l.id)))} available at one location`,
          );
      }
      const take = allowShortfall
        ? Math.min(r.quantity, locationId ? Math.max(0, free(locationId)) : 0)
        : r.quantity;
      if (locationId && free(locationId) < take) {
        reject(
          'INSUFFICIENT_STOCK',
          `line "${r.label ?? ''}": ${free(locationId)} available, ${r.quantity} requested`,
        );
      }
      if (locationId) {
        pending.set(
          `${r.stockItemId}|${locationId}`,
          (pending.get(`${r.stockItemId}|${locationId}`) ?? 0) + take,
        );
      }
      planned.set(line.id, { stockItemId: r.stockItemId, locationId, quantity: take });
    }
    for (const line of order.lines) {
      const plan = planned.get(line.id);
      if (!plan) continue;
      line.locationId = plan.locationId;
      line.waitingQuantity = line.requirement.quantity - plan.quantity || undefined;
      line.allocationId = undefined;
      if (plan.locationId && plan.quantity > 0) {
        line.allocationId = `alloc-${line.id}`;
        this.allocations.set(line.id, {
          stockItemId: plan.stockItemId,
          locationId: plan.locationId,
          quantity: plan.quantity,
        });
      }
    }
    order.state = 'READY_TO_PICK';
    order.releasedBy = MOCK_USER;
    order.releasedAt = new Date().toISOString();
    return this.touch(order);
  }

  async pick(input: PickInput): Promise<PickResult> {
    await wait(60);
    const order = this.order(input.orderId);
    const fingerprint = JSON.stringify([
      input.code.trim(),
      input.lineId ?? '',
      input.quantity ?? 0,
      input.manualOverride ?? null,
    ]);
    const keyed = `${order.id}|${input.idempotencyKey}`;
    const earlier = this.pickKeys.get(keyed);
    if (earlier !== undefined) {
      if (earlier !== fingerprint)
        reject(
          'IDEMPOTENCY_KEY_REUSED',
          'the idempotency key was already used for a different request',
        );
      const pick = order.picks.find((p) => p.id === this.pickIdForKey.get(keyed));
      if (pick) return { order: clone(order), pick: clone(pick), replayed: true };
    }
    if (
      order.state !== 'READY_TO_PICK' &&
      order.state !== 'PICKING' &&
      order.state !== 'READY_TO_HANDOVER'
    ) {
      reject('INVALID_STATE', `an order in ${order.state} can't be picked`);
    }
    const code = input.code.trim();
    if (!code) reject('UNKNOWN_CODE', 'empty code');
    const lineOK = (line: FulfillmentOrderLine) => !input.lineId || line.id === input.lineId;
    const pick: FulfillmentPick = {
      id: mockId('pick'),
      code,
      quantity: 1,
      manualOverride: Boolean(input.manualOverride),
      overrideReason: input.manualOverride?.reason,
      overrideNote: input.manualOverride?.note || undefined,
      pickedBy: MOCK_USER,
      pickedAt: new Date().toISOString(),
      removed: false,
    };

    const item = this.items.get(code) ?? (this.itemsByNorm.get(normalizeSerial(code)) ?? [])[0];
    if (item) {
      if ((input.quantity ?? 0) > 1)
        reject('OVER_QUANTITY', 'a serialized Item is picked one at a time');
      if (order.lines.some((l) => l.itemIds.includes(item.id))) reject('DUPLICATE_SCAN', item.id);
      const matching = order.lines.filter(
        (l) =>
          l.requirement.type === 'SERIALIZED' &&
          lineOK(l) &&
          l.requirement.partIds.includes(item.partId),
      );
      if (matching.length === 0) reject('WRONG_ITEM', `${item.id} is Part ${item.partId}`);
      const open = matching.find((l) => l.itemIds.length < l.requirement.quantity);
      if (!open)
        reject(
          'OVER_QUANTITY',
          `every ${matching[0]?.requirement.label ?? item.partId} is already picked`,
        );
      if (item.status && UNSHIPPABLE.has(item.status))
        reject('ITEM_NOT_AVAILABLE', `${item.id} is ${item.status}`);
      const other = this.reservations.get(item.id);
      if (other && other !== order.id)
        reject('ITEM_ALREADY_ALLOCATED', `${item.id} is on another order`);
      this.reservations.set(item.id, order.id);
      open.itemIds.push(item.id);
      pick.lineId = open.id;
      pick.itemId = item.id;
    } else {
      const qty = input.quantity || 1;
      const stock = this.stockItems.find((s) => stockItemMatchesCode(s, code));
      if (!stock) reject('UNKNOWN_CODE', `"${code}"`);
      const lines = order.lines.filter(
        (l) =>
          l.requirement.type === 'STOCK' && lineOK(l) && l.requirement.stockItemId === stock.id,
      );
      if (lines.length === 0) reject('WRONG_ITEM', stock.sku);
      const line = lines.find((l) => l.requirement.quantity - l.pickedQuantity >= qty);
      if (!line) {
        const most = Math.max(...lines.map((l) => l.requirement.quantity - l.pickedQuantity));
        reject('OVER_QUANTITY', `${qty} ${stock.sku} picked, only ${most} still required`);
      }
      // Only what is reserved can be picked; stock received since release
      // is reserved now.
      if (qty > line.requirement.quantity - line.pickedQuantity - (line.waitingQuantity ?? 0)) {
        this.topUp(line);
      }
      if (qty > line.requirement.quantity - line.pickedQuantity - (line.waitingQuantity ?? 0)) {
        reject(
          'INSUFFICIENT_STOCK',
          `${stock.sku}: ${line.waitingQuantity ?? 0} of ${line.requirement.quantity} still waiting for stock; receive it first`,
        );
      }
      line.pickedQuantity += qty;
      pick.lineId = line.id;
      pick.quantity = qty;
    }
    order.picks.push(pick);
    order.state = order.lines.every(lineComplete) ? 'READY_TO_HANDOVER' : 'PICKING';
    this.pickKeys.set(keyed, fingerprint);
    this.pickIdForKey.set(keyed, pick.id);
    return { order: this.touch(order), pick: clone(pick), replayed: false };
  }

  async amendLine(input: AmendLineInput): Promise<FulfillmentOrder> {
    await wait();
    const order = this.order(input.orderId);
    this.checkVersion(order, input.version);
    if (order.state === 'DRAFT') reject('INVALID_STATE', "a draft's lines are edited directly");
    if (
      order.state !== 'READY_TO_PICK' &&
      order.state !== 'PICKING' &&
      order.state !== 'READY_TO_HANDOVER'
    ) {
      reject('INVALID_STATE', `a ${order.state} order's lines can't change`);
    }
    const index = order.lines.findIndex((l) => l.id === input.lineId);
    const line = order.lines[index];
    if (!line) throw new NotFoundError('Line not found');
    const picked = linePicked(line);
    const before = clone(line.requirement);
    // Plan the new reservation before changing anything, so a shortfall
    // leaves the order as it was.
    const reserve = (requirement: typeof line.requirement, locationId: string | undefined) => {
      if (requirement.type !== 'STOCK' || !requirement.stockItemId) return undefined;
      const held = this.allocations.get(line.id);
      const free = (loc: string) =>
        this.available(requirement.stockItemId ?? '', loc) +
        (held && held.locationId === loc && held.stockItemId === requirement.stockItemId
          ? held.quantity
          : 0);
      const loc =
        locationId ??
        this.locations
          .filter((l) => l.active && free(l.id) >= requirement.quantity)
          .sort((a, b) => free(b.id) - free(a.id))[0]?.id;
      if (!loc || free(loc) < requirement.quantity)
        reject('INSUFFICIENT_STOCK', `not enough available for "${requirement.label ?? ''}"`);
      return {
        stockItemId: requirement.stockItemId,
        locationId: loc,
        quantity: requirement.quantity,
      };
    };
    switch (input.action) {
      case 'REDUCE_QUANTITY': {
        if (input.quantity <= 0 || input.quantity >= line.requirement.quantity) {
          reject(
            'INVALID_REFERENCE',
            `the new quantity must be between 1 and ${line.requirement.quantity - 1}`,
          );
        }
        if (input.quantity < picked)
          reject('INVALID_STATE', `${picked} already picked; remove picks first`);
        if (line.waitingQuantity) {
          // A waiting line keeps what it has reserved and waits for less.
          const held = this.allocations.get(line.id);
          const reserved = Math.min(held?.quantity ?? 0, input.quantity);
          if (held) this.allocations.set(line.id, { ...held, quantity: reserved });
          line.waitingQuantity = input.quantity - reserved || undefined;
          line.requirement.quantity = input.quantity;
          break;
        }
        const plan = reserve({ ...line.requirement, quantity: input.quantity }, line.locationId);
        line.requirement.quantity = input.quantity;
        if (plan) this.allocations.set(line.id, plan);
        break;
      }
      case 'SUBSTITUTE': {
        if (picked > 0) reject('INVALID_STATE', `${picked} already picked; remove picks first`);
        this.validateLines([{ requirement: input.requirement }]);
        const plan = reserve(input.requirement, input.locationId || undefined);
        this.allocations.delete(line.id);
        line.requirement = clone(input.requirement);
        line.locationId = plan?.locationId;
        line.allocationId = plan ? `alloc-${line.id}` : undefined;
        line.waitingQuantity = undefined;
        if (plan) this.allocations.set(line.id, plan);
        break;
      }
      case 'CANCEL_LINE':
        if (picked > 0) reject('INVALID_STATE', `${picked} already picked; remove picks first`);
        if (order.lines.length === 1)
          reject('INVALID_STATE', "this is the order's only line; cancel the order instead");
        this.allocations.delete(line.id);
        order.lines.splice(index, 1);
        break;
    }
    order.amendments.push({
      lineId: line.id,
      action: input.action,
      before,
      after: input.action === 'CANCEL_LINE' ? undefined : clone(line.requirement),
      reason: input.reason.trim(),
      amendedBy: MOCK_USER,
      amendedAt: new Date().toISOString(),
    });
    if (order.state !== 'READY_TO_PICK')
      order.state = order.lines.every(lineComplete) ? 'READY_TO_HANDOVER' : 'PICKING';
    return this.touch(order);
  }

  async removePick(orderId: string, pickId: string, reason: string): Promise<FulfillmentOrder> {
    await wait();
    const order = this.order(orderId);
    const pick = order.picks.find((p) => p.id === pickId);
    if (!pick) throw new NotFoundError('Pick not found');
    if (pick.removed) return clone(order);
    if (order.state !== 'PICKING' && order.state !== 'READY_TO_HANDOVER') {
      reject('INVALID_STATE', `picks can't be removed from an order in ${order.state}`);
    }
    const line = order.lines.find((l) => l.id === pick.lineId);
    if (line) {
      if (pick.itemId) {
        line.itemIds = line.itemIds.filter((id) => id !== pick.itemId);
        this.reservations.delete(pick.itemId);
      } else {
        line.pickedQuantity -= pick.quantity;
      }
    }
    pick.removed = true;
    pick.removedBy = MOCK_USER;
    pick.removedAt = new Date().toISOString();
    pick.removeReason = reason.trim() || undefined;
    order.state = 'PICKING';
    return this.touch(order);
  }

  async cancelOrder(id: string, version: number, reason: string): Promise<FulfillmentOrder> {
    await wait();
    const order = this.order(id);
    this.checkVersion(order, version);
    if (order.state === 'COMPLETED' || order.state === 'CANCELLED') {
      reject('INVALID_STATE', `a ${order.state} order can't be cancelled`);
    }
    for (const line of order.lines) {
      this.allocations.delete(line.id);
      for (const itemId of line.itemIds) this.reservations.delete(itemId);
    }
    order.state = 'CANCELLED';
    order.cancelReason = reason.trim();
    order.cancelledBy = MOCK_USER;
    order.cancelledAt = new Date().toISOString();
    return this.touch(order);
  }

  /** "SKU Name", as leatherback names a stock item in its errors. */
  private stockName(stockItemId: string | undefined): string {
    const item = this.stockItems.find((candidate) => candidate.id === stockItemId);
    return item ? `${item.sku} ${item.displayName}`.trim() : (stockItemId ?? '');
  }

  async completeOrder(
    id: string,
    version: number,
    recipient: string,
    shipmentId?: string,
    method: HandoverMethod = 'CARRIER',
  ): Promise<FulfillmentOrder> {
    await wait();
    const order = this.order(id);
    this.checkVersion(order, version);
    if (method === 'TECHNICIAN' && (shipmentId || order.shipmentId))
      reject('INVALID_STATE', "an order handed to a technician doesn't leave on a shipment");
    if (method === 'TECHNICIAN' && !recipient.trim())
      reject('INVALID_STATE', 'recipient must be set: the technician taking the order');
    if (order.state === 'READY_TO_PICK' || order.state === 'PICKING')
      reject('INCOMPLETE', 'order is not completely picked');
    if (order.state !== 'READY_TO_HANDOVER')
      reject('INVALID_STATE', `a ${order.state} order can't be handed over`);
    // Like leatherback: every picked item is checked before anything moves,
    // and all that can no longer ship are named with their line.
    const unavailable = order.lines.flatMap((line) =>
      line.itemIds
        .map((itemId) => this.items.get(itemId))
        .filter((item): item is MockItem => Boolean(item?.status && UNSHIPPABLE.has(item.status)))
        .map((item) =>
          line.requirement.label
            ? `${item.id} (${line.requirement.label}) is now ${item.status}`
            : `${item.id} is now ${item.status}`,
        ),
    );
    if (unavailable.length > 0) reject('ITEM_NOT_AVAILABLE', unavailable.sort().join('; '));
    for (const line of order.lines) {
      const allocation = this.allocations.get(line.id);
      if (allocation) {
        const key = `${allocation.stockItemId}|${allocation.locationId}`;
        this.onHand.set(key, (this.onHand.get(key) ?? 0) - allocation.quantity);
        this.allocations.delete(line.id);
        this.movements.push({
          id: mockId('movement'),
          stockItemId: allocation.stockItemId,
          quantity: allocation.quantity,
          fromLocationId: allocation.locationId,
          reason: 'FULFILLMENT_ISSUE',
          referenceType: 'FULFILLMENT_ORDER_LINE',
          referenceId: line.id,
          actor: MOCK_USER,
          createTime: new Date().toISOString(),
        });
      }
      for (const itemId of line.itemIds) {
        const item = this.items.get(itemId);
        if (item) {
          item.siteId = order.siteId;
          item.status = method === 'TECHNICIAN' ? 'DELIVERED_TO_CUSTOMER' : 'READY_TO_SHIP';
        }
        this.reservations.delete(itemId);
      }
    }
    order.state = 'COMPLETED';
    order.recipient = recipient.trim() || undefined;
    order.handoverMethod = method;
    order.shipmentId = shipmentId || order.shipmentId;
    order.handedOverBy = MOCK_USER;
    order.handedOverAt = new Date().toISOString();
    return this.touch(order);
  }

  async listTemplates(includeInactive = false): Promise<FulfillmentTemplate[]> {
    await wait();
    return clone(this.templates.filter((t) => includeInactive || t.active));
  }

  async createTemplate(input: FulfillmentTemplateInput): Promise<FulfillmentTemplate> {
    await wait();
    this.validateLines(input.requirements.map((requirement) => ({ requirement })));
    const template: FulfillmentTemplate = {
      id: mockId('template'),
      name: input.name.trim(),
      description: input.description?.trim() || undefined,
      active: input.active,
      requirements: clone(input.requirements),
      version: 0,
      lastModified: new Date().toISOString(),
      lastModifiedBy: MOCK_USER,
    };
    this.templates.push(template);
    return clone(template);
  }

  async updateTemplate(
    id: string,
    version: number,
    input: FulfillmentTemplateInput,
  ): Promise<FulfillmentTemplate> {
    await wait();
    const template = this.templates.find((t) => t.id === id);
    if (!template) throw new NotFoundError('Template not found');
    if (template.version !== version)
      throw new ConflictError('fulfillment template was changed by someone else', template.version);
    this.validateLines(input.requirements.map((requirement) => ({ requirement })));
    template.name = input.name.trim();
    if (input.description !== undefined) {
      template.description = input.description.trim() || undefined;
    }
    template.active = input.active;
    template.requirements = clone(input.requirements);
    template.version += 1;
    template.lastModified = new Date().toISOString();
    template.lastModifiedBy = MOCK_USER;
    return clone(template);
  }

  async listStockItems(includeInactive = false): Promise<StockItem[]> {
    await wait();
    return clone(this.stockItems.filter((item) => includeInactive || item.active));
  }

  async listWarehouseLocations(
    siteId?: string,
    includeInactive = false,
  ): Promise<WarehouseLocation[]> {
    await wait();
    return clone(
      this.locations.filter(
        (l) => (!siteId || l.siteId === siteId) && (includeInactive || l.active),
      ),
    );
  }

  async listStockBalances(filters?: string | StockBalanceFilters): Promise<StockBalance[]> {
    await wait();
    const f: StockBalanceFilters =
      typeof filters === 'string' ? { stockItemId: filters } : (filters ?? {});
    const out: StockBalance[] = [];
    for (const [key, onHand] of this.onHand) {
      const [itemId = '', locationId = ''] = key.split('|');
      if (f.stockItemId && itemId !== f.stockItemId) continue;
      if (f.locationId && locationId !== f.locationId) continue;
      if (f.siteId && this.locations.find((l) => l.id === locationId)?.siteId !== f.siteId)
        continue;
      const allocated = this.allocated(itemId, locationId);
      out.push({
        stockItemId: itemId,
        locationId,
        onHand,
        allocated,
        available: onHand - allocated,
      });
    }
    return out;
  }

  // Like Leatherback: a barcode belongs to one item and never equals
  // another item's SKU, so a scan finds exactly one item.
  private checkBarcodes(id: string | undefined, sku: string, barcodes: readonly string[]): void {
    const clash = this.stockItems.find(
      (other) =>
        other.id !== id &&
        (barcodes.includes(other.sku) ||
          other.barcodes.includes(sku) ||
          barcodes.some((code) => other.barcodes.includes(code))),
    );
    if (clash) {
      throw new DuplicateError(`stock item ${clash.sku} already uses one of these codes`);
    }
  }

  async createStockItem(input: StockItemInput): Promise<StockItem> {
    await wait();
    const sku = normalizeSku(input.sku);
    if (this.stockItems.some((item) => item.sku === sku)) {
      throw new DuplicateError(`a stock item with SKU "${sku}" already exists`);
    }
    const barcodes = [...new Set((input.barcodes ?? []).map(normalizeBarcode).filter(Boolean))];
    this.checkBarcodes(undefined, sku, barcodes);
    const { usage, minimumQuantity, ...rest } = input;
    const item: StockItem = {
      ...rest,
      id: mockId('stock'),
      sku,
      displayName: input.displayName.trim(),
      barcodes,
      usage: usage || undefined,
      minimumQuantity: minimumQuantity ?? 0,
      version: 0,
    };
    this.stockItems.push(item);
    return clone(item);
  }

  async setStockItemImage(id: string, image: Blob): Promise<StockItem> {
    await wait();
    const item = this.stockItems.find((s) => s.id === id);
    if (!item) throw new NotFoundError('Stock item not found');
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(image.type)) {
      throw new ValidationError('content_type must be image/jpeg, image/png or image/webp');
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => reject(reader.error ?? new Error('The photo could not be read.'));
      reader.readAsDataURL(image);
    });
    this.stockImages.set(id, dataUrl);
    item.imageId = `img-${this.nextImageId++}`;
    item.version += 1;
    return clone(item);
  }

  async deleteStockItemImage(id: string): Promise<StockItem> {
    await wait();
    const item = this.stockItems.find((s) => s.id === id);
    if (!item) throw new NotFoundError('Stock item not found');
    if (item.imageId) {
      this.stockImages.delete(id);
      item.imageId = undefined;
      item.version += 1;
    }
    return clone(item);
  }

  stockItemImageUrl(item: StockItem): string | undefined {
    return item.imageId ? this.stockImages.get(item.id) : undefined;
  }

  async updateStockItem(id: string, version: number, input: StockItemInput): Promise<StockItem> {
    await wait();
    const item = this.stockItems.find((s) => s.id === id);
    if (!item) throw new NotFoundError('Stock item not found');
    if (item.version !== version)
      throw new ConflictError('stock item was changed by someone else', item.version);
    const sku = normalizeSku(input.sku);
    if (this.stockItems.some((other) => other.id !== id && other.sku === sku)) {
      throw new DuplicateError(`a stock item with SKU "${sku}" already exists`);
    }
    const barcodes =
      input.barcodes === undefined
        ? item.barcodes
        : [...new Set(input.barcodes.map(normalizeBarcode).filter(Boolean))];
    this.checkBarcodes(id, sku, barcodes);
    // Like the backend, an omitted description is left unchanged and '' clears it.
    const { description, usage, minimumQuantity, ...rest } = input;
    Object.assign(item, {
      ...rest,
      sku,
      displayName: input.displayName.trim(),
      barcodes,
      version: item.version + 1,
    });
    if (description !== undefined) item.description = description.trim() || undefined;
    if (usage !== undefined) item.usage = usage || undefined;
    if (minimumQuantity !== undefined) item.minimumQuantity = minimumQuantity;
    return clone(item);
  }

  async createWarehouseLocation(input: WarehouseLocationInput): Promise<WarehouseLocation> {
    await wait();
    const code = normalizeSku(input.code);
    const parentId = input.parentId || undefined;
    if (
      this.locations.some(
        (l) => l.siteId === input.siteId && l.parentId === parentId && l.code === code,
      )
    ) {
      throw new DuplicateError(`location code "${code}" is already used here`);
    }
    const location: WarehouseLocation = {
      id: mockId('loc'),
      siteId: input.siteId,
      parentId,
      type: input.type,
      code,
      displayName: input.displayName?.trim() || undefined,
      active: input.active,
      version: 0,
    };
    this.locations.push(location);
    return clone(location);
  }

  async updateWarehouseLocation(
    id: string,
    version: number,
    input: Pick<WarehouseLocationInput, 'code' | 'displayName' | 'type' | 'active'>,
  ): Promise<WarehouseLocation> {
    await wait();
    const location = this.locations.find((l) => l.id === id);
    if (!location) throw new NotFoundError('Location not found');
    if (location.version !== version)
      throw new ConflictError('location was changed by someone else', location.version);
    if (!input.active && location.active) {
      const held = [...this.onHand].some(([key, qty]) => key.endsWith(`|${id}`) && qty > 0);
      if (held) reject('INVALID_STATE', 'location still holds stock');
    }
    Object.assign(location, {
      code: normalizeSku(input.code),
      displayName: input.displayName?.trim() || undefined,
      type: input.type,
      active: input.active,
      version: location.version + 1,
    });
    return clone(location);
  }

  async listStockMovements(
    filters?: StockMovementFilters,
    pageToken?: string,
  ): Promise<StockMovementPage> {
    await wait();
    const matching = [...this.movements]
      .reverse()
      .filter(
        (m) =>
          (!filters?.stockItemId || m.stockItemId === filters.stockItemId) &&
          (!filters?.locationId ||
            m.fromLocationId === filters.locationId ||
            m.toLocationId === filters.locationId),
      );
    const start = pageToken ? Number(pageToken) : 0;
    const page = matching.slice(start, start + 50);
    return {
      movements: clone(page),
      nextPageToken: start + 50 < matching.length ? String(start + 50) : undefined,
    };
  }

  async recordStockCommand(command: StockCommand): Promise<StockCommandResult> {
    await wait();
    const fingerprint = JSON.stringify({ ...command, idempotencyKey: undefined });
    const earlier = this.commandKeys.get(command.idempotencyKey);
    if (earlier) {
      if (earlier[0] !== fingerprint)
        reject(
          'IDEMPOTENCY_KEY_REUSED',
          'the idempotency key was already used for a different request',
        );
      const movement = this.movements.find((m) => m.id === earlier[1]);
      if (movement)
        return {
          movement: clone(movement),
          replayed: true,
          balances: await this.affected(movement),
        };
    }
    if (command.kind === 'adjust' && !command.note?.trim())
      throw new ValidationError('note must say why the balance was wrong');
    const item = this.stockItems.find((s) => s.id === command.stockItemId);
    if (!item) throw new NotFoundError('stock item not found');
    let from: string | undefined;
    let to: string | undefined;
    let quantity: number;
    switch (command.kind) {
      case 'transfer':
        from = command.fromLocationId;
        to = command.toLocationId;
        quantity = command.quantity;
        break;
      case 'adjust':
        if (command.quantityDelta > 0) to = command.locationId;
        else from = command.locationId;
        quantity = Math.abs(command.quantityDelta);
        break;
      default:
        to = command.locationId;
        quantity = command.quantity;
    }
    if (to && !item.active) reject('INACTIVE', `stock item ${item.sku}`);
    if (to && !this.locations.find((l) => l.id === to)?.active) reject('INACTIVE', 'location');
    if (from) {
      const available = this.available(item.id, from);
      if (quantity > available)
        reject('INSUFFICIENT_STOCK', `${available} available, ${quantity} requested`);
      this.onHand.set(
        `${item.id}|${from}`,
        (this.onHand.get(`${item.id}|${from}`) ?? 0) - quantity,
      );
    }
    if (to)
      this.onHand.set(`${item.id}|${to}`, (this.onHand.get(`${item.id}|${to}`) ?? 0) + quantity);
    const reasons = {
      receive: 'GOODS_RECEIPT',
      transfer: 'TRANSFER',
      return: 'RETURN',
      adjust: 'ADJUSTMENT',
      openingBalance: 'OPENING_BALANCE',
    } as const;
    const movement: StockMovement = {
      id: mockId('movement'),
      stockItemId: item.id,
      quantity,
      fromLocationId: from,
      toLocationId: to,
      reason: reasons[command.kind],
      referenceType:
        'referenceType' in command ? command.referenceType?.trim() || undefined : undefined,
      referenceId: 'referenceId' in command ? command.referenceId?.trim() || undefined : undefined,
      note: command.note?.trim() || undefined,
      actor: MOCK_USER,
      createTime: new Date().toISOString(),
    };
    this.movements.push(movement);
    this.commandKeys.set(command.idempotencyKey, [fingerprint, movement.id]);
    return { movement: clone(movement), replayed: false, balances: await this.affected(movement) };
  }

  private async affected(movement: StockMovement): Promise<StockBalance[]> {
    const all = await this.listStockBalances({ stockItemId: movement.stockItemId });
    return all.filter(
      (b) => b.locationId === movement.fromLocationId || b.locationId === movement.toLocationId,
    );
  }
}

const mock = new MockFulfillmentApi();
export const fulfillmentApi: FulfillmentApi = mock;
/** The same instance, so stock received here can be picked in pick-pack. */
export const stockApi: StockApi = mock;
